/**
 * The live translator's `live_translate_sentence` count, sent in batches (SEO_BRIEF_10 section
 * 2.6). A beacon a sentence cost three rows written at the edge each (the day's count, the
 * country's, a step on the visitor's path), so one ten-minute conversation wrote as much as
 * twenty ordinary visits, and a quick talker ran into the Worker's 30 beacons a minute per
 * address, which then dropped that address's other beacons as well.
 *
 * So each sentence is tallied here, per engine and language pair, and a tally goes out as one
 * beacon carrying how many it holds (`sentences`, at most lib/usage.ts LIVE_SENTENCE_BATCH):
 * when it reaches that many, and with whatever is left whenever the page is hidden or left
 * (`onPageHide`). A tab the browser throws away without either loses what it held, as a
 * `no_result` does.
 */

import { LIVE_SENTENCE_BATCH, type LiveTranslateEngine } from '../usage.ts';

/** One batch out: how many sentences `engine` translated from `source` into `target`. */
export type SendSentences = (
  engine: LiveTranslateEngine,
  source: string,
  target: string,
  sentences: number,
) => void;

export interface SentenceCount {
  /** One sentence translated by `engine` from `source` into `target`. */
  add(engine: LiveTranslateEngine, source: string, target: string): void;
  /** Every tally out now, one beacon each, and the tallies emptied. */
  flush(): void;
}

interface Tally {
  readonly engine: LiveTranslateEngine;
  readonly source: string;
  readonly target: string;
  n: number;
}

export function createSentenceCount(
  send: SendSentences,
  batch: number = LIVE_SENTENCE_BATCH,
): SentenceCount {
  const tallies = new Map<string, Tally>();

  function out(key: string): void {
    const tally = tallies.get(key);
    if (!tally) return;
    tallies.delete(key);
    send(tally.engine, tally.source, tally.target, tally.n);
  }

  return {
    add(engine, source, target) {
      const key = `${engine}|${source}|${target}`;
      const tally = tallies.get(key) ?? { engine, source, target, n: 0 };
      tally.n += 1;
      tallies.set(key, tally);
      if (tally.n >= batch) out(key);
    },
    flush() {
      for (const key of [...tallies.keys()]) out(key);
    },
  };
}

/** The page's own events, as `onPageHide` reads them: `globalThis` in a browser. */
export interface PageEvents {
  readonly document?:
    | {
        readonly visibilityState: string;
        addEventListener(type: 'visibilitychange', listener: () => void): void;
      }
    | undefined;
  addEventListener?(type: 'pagehide', listener: () => void): void;
}

/**
 * `fn` whenever the page is hidden (another tab, another app, the screen off) or left. On a
 * phone a hide is often the last thing a page hears, so both are listened to.
 */
export function onPageHide(fn: () => void, scope: PageEvents = globalThis as PageEvents): void {
  const doc = scope.document;
  doc?.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') fn();
  });
  if (typeof scope.addEventListener === 'function') scope.addEventListener('pagehide', fn);
}
